import { criarSessao, lerSessao, limparSessao, salvarSessao } from "../Auth/sessao";

// Unico lugar que conhece a URL da api-btk. So as chamadas feitas por aqui
// levam o token: servicos de terceiros (ViaCEP, OpenCEP) nunca o recebem.
const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

export const ERRO_CONEXAO =
  "Não foi possível falar com o servidor. Verifique a conexão e tente novamente.";

// Renova um pouco antes de vencer para a requisicao nao chegar com o token expirado.
const MARGEM_RENOVACAO_MS = 30 * 1000;
const LOCK_RENOVACAO = "erp-btk:renovar-sessao";

export class SessaoExpiradaErro extends Error {
  constructor() {
    super("Sua sessão expirou. Entre novamente para continuar.");
    this.name = "SessaoExpiradaErro";
  }
}

const vencendo = (sessao) => sessao.accessTokenExpiraEm - Date.now() < MARGEM_RENOVACAO_MS;

async function enviar(caminho, opcoes) {
  try {
    return await fetch(`${API_URL}${caminho}`, opcoes);
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new Error(ERRO_CONEXAO);
  }
}

async function renovar(accessTokenUsado) {
  const sessao = lerSessao();
  if (!sessao) throw new SessaoExpiradaErro();

  // Outra requisicao ou aba ja renovou enquanto esta esperava a vez.
  if (sessao.accessToken !== accessTokenUsado && !vencendo(sessao)) {
    return sessao;
  }

  const resposta = await enviar("/Auth/Refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: sessao.refreshToken }),
  });

  if (resposta.status === 401 || resposta.status === 422) {
    limparSessao("expirada");
    throw new SessaoExpiradaErro();
  }

  // Erro do servidor nao e motivo para derrubar a sessao: a proxima tenta de novo.
  const nova = resposta.ok
    ? criarSessao(await resposta.json().catch(() => null), sessao.usuario)
    : null;
  if (!nova) throw new Error(ERRO_CONEXAO);

  salvarSessao(nova);
  return nova;
}

// O refresh token vale uma vez so: duas renovacoes com o mesmo token fazem a
// api-btk revogar a sessao. Por isso ha uma renovacao por vez na aba (promessa
// compartilhada) e entre abas (Web Locks, quando o navegador suporta).
let renovacaoEmAndamento = null;

function renovarSessao(accessTokenUsado) {
  if (!renovacaoEmAndamento) {
    const tarefa = () => renovar(accessTokenUsado);
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;

    renovacaoEmAndamento = (locks ? locks.request(LOCK_RENOVACAO, tarefa) : tarefa()).finally(
      () => {
        renovacaoEmAndamento = null;
      }
    );
  }
  return renovacaoEmAndamento;
}

const comToken = (opcoes, headers, token) => ({
  ...opcoes,
  headers: { ...headers, Authorization: `Bearer ${token}` },
});

/**
 * fetch para a api-btk: monta a URL, envia o access token e renova a sessao
 * quando preciso. Devolve a Response, como o fetch.
 * @param {string} caminho ex.: "/ERP/Propostas?pagina=2"
 * @param {RequestInit & { autenticar?: boolean }} opcoes autenticar: false nas rotas /Auth.
 * @throws Error(ERRO_CONEXAO) sem rede; SessaoExpiradaErro sem sessao valida.
 */
export async function apiFetch(caminho, { autenticar = true, headers, ...opcoes } = {}) {
  if (!autenticar) return enviar(caminho, { ...opcoes, headers });

  let sessao = lerSessao();
  if (!sessao) throw new SessaoExpiradaErro();
  if (vencendo(sessao)) sessao = await renovarSessao(sessao.accessToken);

  let resposta = await enviar(caminho, comToken(opcoes, headers, sessao.accessToken));
  if (resposta.status !== 401) return resposta;

  // O 401 vem do checkToken, antes de a rota rodar: repetir a requisicao e seguro.
  sessao = await renovarSessao(sessao.accessToken);
  resposta = await enviar(caminho, comToken(opcoes, headers, sessao.accessToken));

  if (resposta.status === 401) {
    limparSessao("expirada");
    throw new SessaoExpiradaErro();
  }
  return resposta;
}
